import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';

import { Box, Button, GridLegacy as Grid, Stack, TextField } from '@mui/material';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import Spinner from '~/components/Common/Spinner';
import InputCruiseLineSelect from '~/components/Cruises/components/Selects/InputCruiseLineSelect';
import { CruiseBundle } from '~/components/Cruises/pages/Bundles/types';

import { useSaveBundle } from '~/hooks/apis/cruises/useSaveBundle';

import { CruiseLine } from '~/services/cruises/CruiseLineService';

const INITIAL_FORM_STATE: Partial<CruiseBundle> = {
  name: '',
  markup: 0,
  startDate: null,
  endDate: null,
  fkVendorId: null,
};

type Props = {
  bundle?: CruiseBundle;
};

function BundleForm({ bundle }: Props) {
  const [formValues, setFormValues] = useState<Partial<CruiseBundle>>(INITIAL_FORM_STATE);

  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const isEditing = !!bundle;

  const saveBundleMutation = useSaveBundle();

  const isSaveEnabled = useMemo(
    () =>
      formValues.name &&
      formValues.startDate &&
      formValues.endDate &&
      formValues.fkVendorId &&
      typeof formValues.markup === 'number' &&
      !saveBundleMutation.isPending,
    [formValues, saveBundleMutation.isPending],
  );

  const handleChangeField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name) {
      setFormValues((prev) => ({ ...prev, [name]: value ?? undefined }));
    }
  }, []);

  const handleChangeNumberField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, valueAsNumber } = event.target;
    if (name) {
      setFormValues((prev) => ({ ...prev, [name]: isNaN(valueAsNumber) ? '' : valueAsNumber }));
    }
  }, []);

  const handleChangeDateField = useCallback((value: string, name: 'startDate' | 'endDate') => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCruiseLineField = useCallback((cruiseLine: CruiseLine) => {
    setFormValues((prev) => ({ ...prev, fkVendorId: cruiseLine?.id ?? null }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (formValues.startDate && formValues.endDate && formValues.endDate <= formValues.startDate) {
      enqueueSnackbar('End date must be after start date', { variant: 'error' });
      return;
    }

    const sharedFields = {
      name: formValues.name || '',
      markup: (formValues.markup || 0) / 100,
      startDate: formValues.startDate || '',
      endDate: formValues.endDate || '',
    };

    const apiData = isEditing ? sharedFields : { ...sharedFields, fkVendorId: formValues.fkVendorId || '' };

    saveBundleMutation.mutate(
      { id: bundle?.id, data: apiData },
      {
        onSuccess: (result) => {
          enqueueSnackbar('Bundle saved successfully', { variant: 'success' });
          const bundleId = bundle?.id || result?.id;
          history.push(`/cruises/bundles/edit/${bundleId}/items`);
        },
        onError: () => {
          enqueueSnackbar('Error saving bundle', { variant: 'error' });
        },
      },
    );
  }, [bundle?.id, isEditing, enqueueSnackbar, history, formValues, saveBundleMutation]);

  useEffect(() => {
    if (bundle) {
      setFormValues({
        name: bundle.name,
        markup: bundle.markup * 100,
        startDate: bundle.startDate,
        endDate: bundle.endDate,
        fkVendorId: bundle.fkVendorId,
      });
    }
  }, [bundle]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            required
            fullWidth
            name="name"
            label="Bundle Name"
            onChange={handleChangeField}
            inputProps={{ maxLength: 100 }}
            value={formValues.name}
            helperText="e.g. Sydney Pre & Post Stay Bundle"
          />
        </Grid>
        <Grid item xs={6}>
          <InputCruiseLineSelect
            label="Cruise line"
            value={formValues.fkVendorId}
            onChange={handleCruiseLineField}
            withApprovedOffers
            disabled={saveBundleMutation.isPending}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={1}>
        <Grid item xs={3}>
          <TextField
            required
            fullWidth
            name="markup"
            label="Markup (%)"
            type="number"
            onChange={handleChangeNumberField}
            value={formValues.markup}
            helperText="Percentage to mark up cruise NET price (e.g. 20 for 20%)"
            inputProps={{ min: 0, max: 100, step: 0.1 }}
          />
        </Grid>
      </Grid>

      <Stack mt={2} spacing={2} direction="row">
        <DateTimeWidget
          defaultTimezone="Australia/Sydney"
          disablePast
          format="DD/MM/YYYY HH:mm"
          label="Bundle Start Date *"
          value={formValues.startDate}
          onChange={(value) => handleChangeDateField(value, 'startDate')}
        />

        <DateTimeWidget
          defaultTimezone="Australia/Sydney"
          disablePast
          format="DD/MM/YYYY HH:mm"
          label="Bundle End Date *"
          value={formValues.endDate}
          onChange={(value) => handleChangeDateField(value, 'endDate')}
        />
      </Stack>

      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button variant="contained" onClick={handleSubmit} disabled={!isSaveEnabled}>
          {saveBundleMutation.isPending && <Spinner size={15} />}
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default BundleForm;
